import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Box,
  Avatar,
  Rating,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import CallEndIcon from "@mui/icons-material/CallEnd";
import StarIcon from "@mui/icons-material/Star";
import { Device } from "@twilio/voice-sdk";
import { gql, useMutation } from "@apollo/client";
import client from "../../../../components/ApolloClient";
import { UserInfoContext } from "../../../../components/Routes";

const SAVE_CALL_RATINGS = gql`
  mutation saveCallRatings($saveCallRatingsRequest: SaveCallRatingsRequest!) {
    saveCallRatings(saveCallRatingsRequest: $saveCallRatingsRequest)
  }
`;

const CallDialog = ({ open, onClose, careTeam = [] }) => {
  const [isDialing, setIsDialing] = useState(false);
  const [callStatus, setCallStatus] = useState("");
  const [device, setDevice] = useState(null);
  const [activeConnection, setActiveConnection] = useState(null);
  
  // Timer for auto-disconnect
  const [autoDisconnectTimer, setAutoDisconnectTimer] = useState(null);
  
  // Rating dialog state
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState("");
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  // GraphQL mutation for saving ratings
  const [saveCallRatings] = useMutation(SAVE_CALL_RATINGS);

  const { userInfo } = useContext(UserInfoContext);

  // Determine if we're calling a specific care team member
  const isCareTeamCall = careTeam.length > 0;
  const member = isCareTeamCall ? careTeam[0] : null;
  const hostname = window.location.hostname;
  const title = isCareTeamCall 
    ? `Call ${member.name}`
    : hostname.startsWith('ascension.mpoweredhealth') 
      ? "Contact the nurse hotline" 
      : "Contact your broker";

  const description = isCareTeamCall
    // ? `Connecting you to ${member.role}`
    ? ''
    : hostname.startsWith('ascension.mpoweredhealth') 
      ? "Need help? The nurse hotline is open. Dial now to connect." 
      : "Have questions? Your broker is ready to assist you.";

  // Care managers have no phone number on file, so every call (dummy or real) joins the same support conference line
  const to = hostname.startsWith('cloverhealth.mpoweredhealth')
    ? 'cloversupport'
    : hostname.startsWith('ust.mpoweredhealth')
      ? 'usthealth'
      : 'mpoweredhealth';

  // Clean up Twilio device when component unmounts
  useEffect(() => {
    return () => {
      if (autoDisconnectTimer) {
        clearTimeout(autoDisconnectTimer);
      }
      if (device) {
        device.disconnectAll();
        device.destroy();
      }
    };
  }, []); // Empty deps - only on unmount

  // Cleanup when dialog closes
  useEffect(() => {
    if (!open) {
      console.log("Dialog closed - cleaning up");
      
      // Clear timer
      if (autoDisconnectTimer) {
        clearTimeout(autoDisconnectTimer);
        setAutoDisconnectTimer(null);
      }
      
      // Disconnect active call
      if (activeConnection) {
        activeConnection.disconnect();
        setActiveConnection(null);
      }
      
      // Destroy device
      if (device) {
        console.log("Destroying Twilio device");
        device.disconnectAll();
        device.destroy();
        setDevice(null);
      }
      
      // Reset states
      setIsDialing(false);
      setCallStatus("");
    }
  }, [open]); // Run when 'open' changes

  const fetchToken = async (identity = to, isClientCall = true) => {
    const { data } = await client.query({
      query: gql`
        query fetchClientCallToken($identity: String!, $isClientCall: Boolean!) {
          fetchClientCallToken(identity: $identity, isClientCall: $isClientCall)
        }
      `,
      variables: {
        identity,
        isClientCall,
      },
      fetchPolicy: 'network-only', // Always fetch fresh token
    });
    return data.fetchClientCallToken;
  };

  const initializeDevice = async (token) => {
    const newDevice = new Device(token, {
      logLevel: 1, // 0=trace, 1=debug, 2=info, 3=warn, 4=error, 5=silent
      codecPreferences: ["opus", "pcmu"],
    });

    newDevice.on("registered", () => {
      console.log("Twilio Device registered and ready");
    });

    newDevice.on("error", (error) => {
      console.error("Twilio Device Error:", error);
      setCallStatus("Connection error: " + error.message);
      setIsDialing(false);
    });

    newDevice.on("tokenWillExpire", async () => {
      console.log("Token will expire, refreshing...");
      try {
        const newToken = await fetchToken();
        newDevice.updateToken(newToken);
      } catch (error) {
        console.error("Error refreshing token:", error);
      }
    });

    // Register the device
    await newDevice.register();
    
    setDevice(newDevice);
    return newDevice;
  };

  const setupCallListeners = (call) => {
    call.on("accept", () => {
      console.log("Call accepted/connected");
      setActiveConnection(call);
      setCallStatus(`Connected to ${member?.name || "support"}`);
      setIsDialing(true);

      // ========================================
      // ⏱️ AUTO-DISCONNECT CALL AFTER 30 SECONDS
      // Commented out to disable auto-disconnect
      // ========================================
      // const timer = setTimeout(() => {
      //   if (call && call.status() === "open") {
      //     console.log("Auto-ending call after 30 seconds");
      //     call.disconnect();
      //   }
      // }, 30000);
      // setAutoDisconnectTimer(timer);
      // ========================================
    });

    call.on("disconnect", () => {
      console.log("Call disconnected");
      if (autoDisconnectTimer) {
        clearTimeout(autoDisconnectTimer);
        setAutoDisconnectTimer(null);
      }
      setActiveConnection(null);
      setIsDialing(false);
      setCallStatus("Call ended");
      
      // Show rating dialog after call ends
      setShowRatingDialog(true);
    });

    call.on("cancel", () => {
      console.log("Call cancelled");
      if (autoDisconnectTimer) {
        clearTimeout(autoDisconnectTimer);
        setAutoDisconnectTimer(null);
      }
      setActiveConnection(null);
      setIsDialing(false);
      setCallStatus("Call cancelled");
    });

    call.on("reject", () => {
      console.log("Call rejected");
      if (autoDisconnectTimer) {
        clearTimeout(autoDisconnectTimer);
        setAutoDisconnectTimer(null);
      }
      setActiveConnection(null);
      setIsDialing(false);
      setCallStatus("Call rejected");
    });

    call.on("error", (error) => {
      console.error("Call error:", error);
      if (autoDisconnectTimer) {
        clearTimeout(autoDisconnectTimer);
        setAutoDisconnectTimer(null);
      }
      setCallStatus(`Call error: ${error.message}`);
      setIsDialing(false);
    });
  };

  const handleCall = async () => {
    setIsDialing(true);
    setCallStatus(isCareTeamCall 
      ? `Connecting to ${member.name}...` 
      : "Connecting to support...");

    try {
      const identity = userInfo?.phone || userInfo?.phoneNumber;
      const token = await fetchToken(identity);

      // Always initialize a new device for each call
      console.log("Initializing device for new call");
      const twilioDevice = await initializeDevice(token);

      const connectOptions = {
        params: {
          To: isCareTeamCall ? member?.phone ? member.phone : to : to
        }
      };

      const call = await twilioDevice.connect(connectOptions);
      setupCallListeners(call);
      setActiveConnection(call);

    } catch (error) {
      console.error("Error making call:", error);
      setCallStatus(`Call failed: ${error.message}`);
      setIsDialing(false);
    }
  };

  const handleEndCall = () => {
    if (autoDisconnectTimer) {
      clearTimeout(autoDisconnectTimer);
      setAutoDisconnectTimer(null);
    }

    if (activeConnection) {
      activeConnection.disconnect();
    } else if (device) {
      device.disconnectAll();
    }
    
    setIsDialing(false);
    setCallStatus("");
    setActiveConnection(null);
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      alert("Please select a rating before submitting");
      return;
    }

    setIsSubmittingRating(true);
    
    try {
      await saveCallRatings({
        variables: {
          saveCallRatingsRequest: {
            rating: rating,
            notes: notes || null,
          },
        },
      });
      
      // Reset rating dialog state
      setShowRatingDialog(false);
      setRating(0);
      setNotes("");
      
      // Close the main dialog
      onClose();
      
    } catch (error) {
      console.error("Error submitting rating:", error);
      alert("Failed to submit rating. Please try again.");
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const handleSkipRating = () => {
    setShowRatingDialog(false);
    setRating(0);
    setNotes("");
    onClose();
  };

  const handleClose = () => {
    if (isDialing) {
      handleEndCall();
    } else {
      onClose();
    }
  };

  return (
    <>
      {/* Main Call Dialog */}
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleClose();
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: 6,
            bgcolor: "#f9fafb",
            p: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            textAlign: "center",
            pb: 0,
            position: "relative",
          }}
        >
          {isDialing ? "Dialing..." : title}
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#aaa",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
            {isCareTeamCall && (
              <Box textAlign="center" mb={2}>
                <Avatar sx={{ width: 56, height: 56, margin: '0 auto 8px' }}>
                  {member.name.charAt(0)}
                </Avatar>
                <Typography variant="subtitle1">{member.name}</Typography>
              </Box>
            )}
            <Typography textAlign="center" color="text.secondary" mt={1}>
              {callStatus || description}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
          {isDialing ? (
            <Button
              variant="contained"
              color="error"
              startIcon={<CallEndIcon />}
              onClick={handleEndCall}
              sx={{
                px: 4,
                py: 1,
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: 2,
              }}
            >
              End call
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<PhoneIcon />}
              onClick={handleCall}
              sx={{
                px: 4,
                py: 1,
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #00b894, #00cec9)",
                boxShadow: 2,
                "&:hover": {
                  background: "linear-gradient(135deg, #00cec9, #00b894)",
                },
              }}
            >
              Call now
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog
        open={showRatingDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleSkipRating();
          }
        }}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: 6,
            bgcolor: "#f9fafb",
            p: 2,
            maxWidth: '450px',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            textAlign: "center",
            fontFamily: 'Inter'
          }}
        >
          Rate your call experience
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3} py={2}>
            <Typography style={{fontWeight: 500, fontFamily: 'Inter'}} textAlign="center">
              How would you rate the quality of your call?
            </Typography>

            <Rating
              name="call-rating"
              value={rating}
              onChange={(event, newValue) => setRating(newValue)}
              size="large"
              icon={<StarIcon fontSize="inherit" />}
              emptyIcon={<StarIcon fontSize="inherit" />}
              sx={{
                fontSize: "3rem",
                '& .MuiRating-iconFilled': {
                  color: '#ffd700',
                },
                '& .MuiRating-iconHover': {
                  color: '#ffc107',
                },
              }}
            />
            <Box>
              <Typography style={{fontWeight: 500, fontFamily: 'Inter'}} textAlign='left'>
                You can also leave additional comments below (optional).
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Tell us about your experience..."
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
          <Button
            onClick={handleSkipRating}
            disabled={isSubmittingRating}
            sx={{
              px: 3,
              py: 1,
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 600,
              color: "#666",
              fontFamily: 'Inter'
            }}
          >
            Skip
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitRating}
            disabled={isSubmittingRating}
            sx={{
              px: 4,
              py: 1,
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 600,
              background: "#036fcb",
              boxShadow: 2,
              fontFamily: 'Inter'
            }}
          >
            {isSubmittingRating ? "Submitting..." : "Submit rating"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CallDialog;